import {
  Assessment,
  AssessmentType,
  Dimension,
  Question,
  Response,
  Section,
  Element,
  ElementKind,
} from '@playbooks/core/src/interfaces/ActionCard'

import { MAX_RANK } from './constants'
import isConditionSatisfied from './isConditionSatisfied'
import isDropdownQuestion from './isDropdownQuestion'
import isFileQuestion from './isFileQuestion'
import isSignatureQuestion from './isSignatureQuestion'
import { DimensionsData } from './types'
import { sectionTypes } from '@playbooks/core/src/constants'

export function getDimensionsMap(scoringDimensions?: Dimension[]) {
  return (
    scoringDimensions?.reduce<DimensionsData>(
      (acc, current) => {
        acc.data[current.id] = current
        return acc
      },
      { sumOffCoefficients: scoringDimensions.reduce((acc, cur) => acc + cur.coefficient, 0), data: {} },
    ) || ({} as DimensionsData)
  )
}

export function handleQuestionsForDimensions(question: Question) {
  if (isDropdownQuestion(question)) {
    const val = question.data?.find(item => item.id === question.value)?.rank || 0
    question.dimensionRelatedScore = !!val ? val : 0
    return question
  }

  if (isFileQuestion(question)) {
    question.dimensionRelatedScore = !!question.value?.length ? question.rank : 0
    return question
  }

  if (isSignatureQuestion(question)) {
    question.dimensionRelatedScore = !!question.value?.points.length ? question.rank : 0
    return question
  }

  question.dimensionRelatedScore = !!question.value ? question.rank : 0
  return question
}

export function handleAssessmentForDimensions(assess: Assessment) {
  if (assess.type === AssessmentType.MULTI) {
    getMultiAssessmentRank(assess)
    return assess
  }
  getSingleAssessmentRank(assess)
  return assess
}

export function getResponsesCalcData(responses: Response[]) {
  return responses.reduce(
    (acc, resp) => {
      if (resp.checked) {
        acc.sumOfSelectedRanks += resp.rank
      }
      acc.sumOfAllRanks += resp.rank
      return acc
    },
    { sumOfSelectedRanks: 0, sumOfAllRanks: 0 },
  )
}

export function getMultiAssessmentMaxRank(assessment: Assessment) {
  if (assessment.altScoringMax || assessment.altScoring) {
    const score = assessment.responses.toSorted((a, b) => (a.rank < b.rank ? 1 : -1))[0]?.rank || 0
    return score
  }
  return MAX_RANK
}

export function getMultiAssessmentRank(assessment: Assessment) {
  if (assessment.altScoringMax) {
    const allChecked = assessment.responses.filter(r => r.checked)
    if (!allChecked.length) {
      assessment.dimensionRelatedScore = 0
      return 0
    }
    const score = allChecked.sort((a, b) => (a.rank < b.rank ? 1 : -1))[0]?.rank || 0
    assessment.dimensionRelatedScore = score
    return score
  }

  if (assessment.altScoring) {
    const { sumOfSelectedRanks, sumOfAllRanks } = getResponsesCalcData(assessment.responses)
    const countOfSelectedAnswers = assessment.responses.filter(r => r.checked).length
    if (countOfSelectedAnswers === 0) {
      assessment.dimensionRelatedScore = 0
      return 0
    }
    const score = sumOfAllRanks ? sumOfSelectedRanks / countOfSelectedAnswers : 0
    assessment.dimensionRelatedScore = score
    return score
  }

  const { sumOfSelectedRanks, sumOfAllRanks } = getResponsesCalcData(assessment.responses)
  const score = sumOfAllRanks ? (sumOfSelectedRanks / sumOfAllRanks) * MAX_RANK : 0
  assessment.dimensionRelatedScore = score
  return score
}

export function getSingleAssessmentRank(assessment: Assessment) {
  const rank = assessment.responses.find(resp => resp.checked)?.rank || 0
  assessment.dimensionRelatedScore = rank
  return rank
}

export function isDataVisible(element: Element | Section, conditionalItems: number[]) {
  return isConditionSatisfied(element.tags || null, conditionalItems, element.conditionRule || null)
}

export function getCoefficient(dimData: DimensionsData['data'], dimId: number) {
  if (!dimData) {
    console.error('getCoefficient: dimData must not be null or undefined')
  } else if (!dimId || typeof dimId !== 'number') {
    console.error('getCoefficient: Dimension Id must be number')
  } else if (typeof dimData[dimId]?.coefficient === 'undefined') {
    console.error('getCoefficient: coefficient is undefined')
  } else {
    return dimData[dimId].coefficient
  }
  return 0
}

export function isQuestionRelevant(question: Question, conditionalItems: number[]) {
  return isDataVisible(question, conditionalItems)
}

export function isAssessmentRelevant(assess: Assessment, conditionalItems: number[], isDimensionsEnabled = false) {
  if (assess.excludedFromScore) {
    return false
  }

  const isVisible = isDataVisible(assess, conditionalItems)
  const isChecked = !!assess.checked

  if (isDimensionsEnabled && !!assess.scoringDimensionId) {
    return isVisible
  }

  return isVisible && (assess.isAutoCollapse ? isChecked : true)
}

export function getRelevantAssessments(assessment: Assessment[] | undefined | null, conditionalItems: number[]) {
  if (!assessment) return []
  return assessment.filter(assess => isAssessmentRelevant(assess, conditionalItems))
}

export function formSectionScore({ score, rank }: { score: number; rank: number }) {
  return rank != 0 ? (score * MAX_RANK) / rank : 0
}

export function isSectionRelevant(sec: Section, conditionalItems: number[]) {
  if (sec.idOfSep === -1) return false
  if (!sec.idOfSep) return false
  if (sec.type === sectionTypes.SEPARATOR) return false
  if (sec.type === sectionTypes.CONTENT && !sec.elements?.length) return false

  return isContentSectionRelevant(sec, conditionalItems)
}

export function isContentSectionRelevant(sec: Section, conditionalItems: number[]) {
  if (isDataVisible(sec, conditionalItems)) {
    if (!sec.elements?.length) return false

    // Filtered elements which have a rating (not recommendations)
    const elementsWithRating = sec.elements.filter(el => el.kind !== ElementKind.RECOMMENDATION)

    if (!elementsWithRating.length) return false

    const hasVisibleElements = elementsWithRating.some(el => isDataVisible(el, conditionalItems))
    return hasVisibleElements
  }
  return false
}
